import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import { localDateString, todayLocal } from "../utils/dateTz";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const FREQUENCIES = [
  { value: "one-time", label: "One-Time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "#6b7280" },
  { value: "medium", label: "Medium", color: "#d97706" },
  { value: "high", label: "High", color: "#dc2626" },
];

const CATEGORIES = ["General", "License", "Tax", "Insurance", "Vendor", "Maintenance", "Payroll", "Compliance", "Other"];

const FILTERS = [
  { value: "all", label: "All" },
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Overdue" },
  { value: "completed", label: "Completed" },
];

const freqBadge = (f) => {
  const map = {
    "one-time": { bg: "secondary", text: "One-Time" },
    daily: { bg: "info", text: "Daily" },
    weekly: { bg: "primary", text: "Weekly" },
    monthly: { bg: "success", text: "Monthly" },
    yearly: { bg: "warning", text: "Yearly" },
  };
  const m = map[f] || { bg: "secondary", text: f };
  return <Badge bg={m.bg}>{m.text}</Badge>;
};

const priorityDot = (p) => {
  const c = PRIORITIES.find((pr) => pr.value === p)?.color || "#6b7280";
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: c,
        marginRight: 6,
      }}
      title={p}
    />
  );
};

const statCard = (label, value, color) => (
  <div
    style={{
      flex: 1,
      background: "#fff",
      borderRadius: 12,
      padding: "20px 22px",
      border: "1px solid #e5e7eb",
      minWidth: 150,
    }}
  >
    <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: "1.5rem", fontWeight: 700, color }}>{value}</div>
  </div>
);

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const daysUntil = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / 86400000);
};

const dueLabel = (d) => {
  const n = daysUntil(d);
  if (n < 0) return <span style={{ color: "#dc2626", fontWeight: 600 }}>Overdue by {Math.abs(n)}d</span>;
  if (n === 0) return <span style={{ color: "#d97706", fontWeight: 600 }}>Due today</span>;
  if (n <= 7) return <span style={{ color: "#d97706" }}>In {n} day{n > 1 ? "s" : ""}</span>;
  return <span className="text-muted">{fmtDate(d)}</span>;
};

const emptyForm = {
  title: "",
  description: "",
  frequency: "one-time",
  dueDate: todayLocal(),
  time: "09:00",
  category: "General",
  priority: "medium",
  notifyWhatsapp: true,
  whatsappGroupId: "",
};

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState("upcoming");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);   // files chosen before first save
  const [uploading, setUploading] = useState(false);

  // WhatsApp groups available for dispatch (from WA_GROUPS env on the backend).
  const [waGroups, setWaGroups] = useState([]);
  const [waDefaultGroupId, setWaDefaultGroupId] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}api/reminders`, { params: { filter } });
      if (!data.success) throw new Error(data.message);
      setReminders(data.reminders);
      setSummary(data.summary);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch WhatsApp groups once on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}api/whatsapp/groups`).then(({ data }) => {
      if (cancelled || !data.success) return;
      setWaGroups(data.groups || []);
      setWaDefaultGroupId(data.defaultGroupId || "");
    }).catch(() => { /* groups dropdown will be hidden if fetch fails */ });
    return () => { cancelled = true; };
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, whatsappGroupId: waDefaultGroupId });
    setPendingFiles([]);
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title,
      description: r.description || "",
      frequency: r.frequency,
      dueDate: localDateString(new Date(r.dueDate)),
      time: r.time || "09:00",
      category: r.category || "General",
      priority: r.priority || "medium",
      // Pre-existing reminders don't have these fields — treat absent as
      // "notify on, use default group" so the form mirrors the scheduler's
      // backwards-compat behaviour.
      notifyWhatsapp: r.notifyWhatsapp === undefined ? true : !!r.notifyWhatsapp,
      whatsappGroupId: r.whatsappGroupId || waDefaultGroupId,
    });
    setPendingFiles([]);
    setShowModal(true);
  };

  const uploadFiles = async (reminderId, files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    await axios.post(`${API_URL}api/reminders/${reminderId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const handleRemoveAttachment = async (reminderId, attachmentId) => {
    try {
      await axios.delete(`${API_URL}api/reminders/${reminderId}/attachments/${attachmentId}`);
      toast.success("Attachment removed");
      // refresh editing object so modal shows updated list
      const { data } = await axios.get(`${API_URL}api/reminders`, { params: { filter } });
      if (data.success) {
        setReminders(data.reminders);
        setSummary(data.summary);
        const updated = data.reminders.find((r) => r._id === reminderId);
        if (updated) setEditing(updated);
      }
    } catch {
      toast.error("Failed to remove attachment");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.dueDate) {
      toast.error("Title and due date are required");
      return;
    }
    setSaving(true);
    try {
      let savedId;
      if (editing) {
        const { data } = await axios.put(`${API_URL}api/reminders/${editing._id}`, form);
        if (!data.success) throw new Error(data.message);
        savedId = editing._id;
        toast.success("Reminder updated");
      } else {
        const { data } = await axios.post(`${API_URL}api/reminders`, form);
        if (!data.success) throw new Error(data.message);
        savedId = data.reminder._id;
        toast.success("Reminder created");
      }
      // Upload any pending files
      if (pendingFiles.length > 0 && savedId) {
        setUploading(true);
        await uploadFiles(savedId, pendingFiles);
        setPendingFiles([]);
        setUploading(false);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API_URL}api/reminders/${id}/complete`);
      toast.success("Marked complete");
      fetchData();
    } catch {
      toast.error("Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await axios.delete(`${API_URL}api/reminders/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <h2 style={{ fontWeight: 700, margin: 0 }}>🔔 Reminders</h2>
        <Button variant="primary" size="sm" onClick={openCreate}>
          + New Reminder
        </Button>
      </div>

      {/* Summary cards */}
      <div className="d-flex gap-3 mb-4 flex-wrap">
        {statCard("Active", summary.totalActive || 0, "#2563eb")}
        {statCard("Due This Week", summary.dueSoonCount || 0, "#d97706")}
        {statCard("Overdue", summary.overdueCount || 0, "#dc2626")}
      </div>

      {/* Filter tabs */}
      <div className="d-flex gap-2 mb-3 align-items-center">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? "dark" : "outline-secondary"}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
        {loading && <Spinner size="sm" animation="border" className="ms-2" />}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Reminders table */}
      <div className="card p-0">
        <Table striped bordered hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
          <thead className="table-light">
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Frequency</th>
              <th style={{ textAlign: "center" }}>Priority</th>
              <th>Due Date</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>📎</th>
              <th style={{ width: 170 }}></th>
            </tr>
          </thead>
          <tbody>
            {reminders.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  No reminders found.
                </td>
              </tr>
            ) : (
              reminders.map((r) => {
                const isCompleted = !r.active || r.completedAt;
                return (
                  <tr key={r._id} style={isCompleted ? { opacity: 0.5 } : {}}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.title}</div>
                      {r.description && (
                        <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                          {r.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge bg="light" text="dark" style={{ border: "1px solid #e5e7eb" }}>
                        {r.category || "General"}
                      </Badge>
                    </td>
                    <td>{freqBadge(r.frequency)}</td>
                    <td style={{ textAlign: "center" }}>
                      {priorityDot(r.priority)}
                      <span style={{ fontSize: "0.82rem" }}>{r.priority}</span>
                    </td>
                    <td>{dueLabel(r.dueDate)}</td>
                    <td>
                      {isCompleted ? (
                        <Badge bg="secondary">Done</Badge>
                      ) : daysUntil(r.dueDate) < 0 ? (
                        <Badge bg="danger">Overdue</Badge>
                      ) : daysUntil(r.dueDate) === 0 ? (
                        <Badge bg="warning" text="dark">Due Today</Badge>
                      ) : (
                        <Badge bg="success">Active</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {(r.attachments || []).length > 0 ? (
                        <span title={(r.attachments || []).map((a) => a.filename).join(", ")}>
                          📎 <Badge bg="info">{r.attachments.length}</Badge>
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {!isCompleted && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            title={r.frequency === "one-time" ? "Mark done" : "Complete & advance"}
                            onClick={() => handleComplete(r._id)}
                          >
                            ✓
                          </Button>
                        )}
                        <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                          ✏️
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r._id)}>
                          🗑️
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Reminder" : "New Reminder"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Liquor License Renewal"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                autoFocus
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional notes…"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </Form.Group>

            <div className="d-flex gap-3 flex-wrap">
              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 160 }}>
                <Form.Label>Frequency *</Form.Label>
                <Form.Select value={form.frequency} onChange={(e) => setField("frequency", e.target.value)}>
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 160 }}>
                <Form.Label>Due Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setField("dueDate", e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 120 }}>
                <Form.Label>Time</Form.Label>
                <Form.Control
                  type="time"
                  value={form.time}
                  onChange={(e) => setField("time", e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-3 flex-wrap">
              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 160 }}>
                <Form.Label>Category</Form.Label>
                <Form.Select value={form.category} onChange={(e) => setField("category", e.target.value)}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 160 }}>
                <Form.Label>Priority</Form.Label>
                <Form.Select value={form.priority} onChange={(e) => setField("priority", e.target.value)}>
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {/* ── WhatsApp delivery ── */}
            <div className="d-flex gap-3 flex-wrap align-items-end">
              <Form.Group className="mb-3" style={{ minWidth: 200 }}>
                <Form.Check
                  type="switch"
                  id="notifyWhatsapp"
                  label="Send WhatsApp reminder"
                  checked={!!form.notifyWhatsapp}
                  onChange={(e) => setField("notifyWhatsapp", e.target.checked)}
                />
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 220 }}>
                <Form.Label>WhatsApp group</Form.Label>
                <Form.Select
                  value={form.whatsappGroupId || ""}
                  disabled={!form.notifyWhatsapp || waGroups.length === 0}
                  onChange={(e) => setField("whatsappGroupId", e.target.value)}
                >
                  {waGroups.length === 0 && <option value="">(no groups configured)</option>}
                  {waGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {/* ── Attachments ── */}
            <hr className="my-3" />
            <Form.Label className="fw-semibold">📎 Attachments</Form.Label>

            {/* Existing attachments (when editing) */}
            {editing && (editing.attachments || []).length > 0 && (
              <div className="mb-2">
                {editing.attachments.map((a) => (
                  <div key={a._id} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: "0.88rem" }}>
                    <a
                      href={`${API_URL}api/reminders/${editing._id}/attachments/${a._id}`}
                      target="_blank"
                      rel="noreferrer"
                      title="View / download"
                    >
                      {/^image\//.test(a.mimetype) ? "🖼️" : "📄"} {a.filename}
                    </a>
                    <span className="text-muted">({(a.size / 1024).toFixed(0)} KB)</span>
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 text-danger"
                      onClick={() => handleRemoveAttachment(editing._id, a._id)}
                      title="Remove"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* New file picker */}
            <Form.Control
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => setPendingFiles(Array.from(e.target.files))}
            />
            <Form.Text className="text-muted">
              Upload photos, PDFs, or documents (max 20 MB each). Works from mobile camera roll too.
            </Form.Text>
            {pendingFiles.length > 0 && (
              <div className="mt-1" style={{ fontSize: "0.85rem" }}>
                {pendingFiles.map((f, i) => (
                  <div key={i}>📄 {f.name} ({(f.size / 1024).toFixed(0)} KB)</div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving || uploading}>
              {saving || uploading ? <Spinner size="sm" animation="border" /> : editing ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
